import { zodResolver } from "@hookform/resolvers/zod";
import type { Applicant, Application, JobOpening, Offer } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Mail, Pencil, Plus, Search, Send } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";
import { z } from "zod";
import { ApplicantDetailModal } from "@/components/applicant-detail-modal";
import { DataTable } from "@/components/data-table";
import { EditApplicantDialog } from "@/components/edit-applicant-dialog";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useQueryParams } from "@/hooks/use-query-params";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";

const applicationFormSchema = z
  .object({
    applicantName: z.string().min(2, "Name is required"),
    applicantEmail: z.string().email("Email inválido").optional().or(z.literal("")),
    applicantPhone: z.string().optional(),
    linkedinUrl: z.string().optional(),
    jobOpeningId: z.string().min(1, "Position is required"),
  })
  .refine(
    (data) =>
      (data.applicantEmail && data.applicantEmail.length > 0) ||
      (data.applicantPhone && data.applicantPhone.length > 0) ||
      (data.linkedinUrl && data.linkedinUrl.length > 0),
    {
      message: "Informe pelo menos um contato: Email, Telefone ou LinkedIn",
      path: ["applicantEmail"],
    },
  );

type ApplicationFormValues = z.infer<typeof applicationFormSchema>;

const offerFormSchema = z.object({
  salaryBase: z.string().min(1, "Salary is required"),
  equityShares: z.string().optional(),
  signingBonus: z.string().optional(),
});

type OfferFormValues = z.infer<typeof offerFormSchema>;

type ExtendedApplication = Application & {
  applicant?: Applicant;
  jobOpening?: JobOpening;
  offers?: Offer[];
};

export default function Applicants() {
  const [, navigate] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<ExtendedApplication | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { getParam, removeParam } = useQueryParams();
  const selectedApplicationId = getParam("applicationId");
  const isDetailModalOpen = !!selectedApplicationId;

  const setIsDetailModalOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        removeParam("applicationId");
      }
    },
    [removeParam],
  );

  const [editApp, setEditApp] = useState<ExtendedApplication | null>(null);
  const { toast } = useToast();

  const { data: applications, isLoading } = useQuery(orpc.applications.list.queryOptions()) as {
    data: ExtendedApplication[] | undefined;
    isLoading: boolean;
  };

  const { data: openJobs } = useQuery(
    orpc.jobOpenings.list.queryOptions({ input: { status: "open" } }),
  ) as { data: JobOpening[] | undefined };

  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      applicantName: "",
      applicantEmail: "",
      applicantPhone: "",
      linkedinUrl: "",
      jobOpeningId: "",
    },
  });

  const offerForm = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema),
    defaultValues: {
      salaryBase: "",
      equityShares: "",
      signingBonus: "",
    },
  });

  const createApplication = useMutation({
    mutationFn: async (data: ApplicationFormValues) => {
      return rpcClient.applications.create({
        applicantName: data.applicantName,
        applicantEmail: data.applicantEmail || undefined,
        applicantPhone: data.applicantPhone,
        linkedinUrl: data.linkedinUrl,
        jobOpeningId: data.jobOpeningId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      setIsOpen(false);
      form.reset();
      toast({ title: "Application created successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create application",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const _advanceStage = useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: string }) => {
      return rpcClient.applications.updateStage({ id, stage });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      toast({ title: "Stage updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update stage",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createOffer = useMutation({
    mutationFn: async (data: OfferFormValues & { applicationId: string }) => {
      return rpcClient.offers.create({
        applicationId: data.applicationId,
        salaryBase: data.salaryBase,
        equityShares: data.equityShares ? Number(data.equityShares) : undefined,
        signingBonus: data.signingBonus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      setIsOfferOpen(false);
      offerForm.reset();
      setSelectedApplication(null);
      toast({ title: "Offer created successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create offer",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const sendOffer = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.sendToCandidate({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      toast({ title: "Offer sent to candidate" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send offer", description: error.message, variant: "destructive" });
    },
  });

  const onSubmit = (data: ApplicationFormValues) => {
    createApplication.mutate(data);
  };

  const onOfferSubmit = (data: OfferFormValues) => {
    if (!selectedApplication) return;
    createOffer.mutate({ ...data, applicationId: selectedApplication.id });
  };

  const filteredApplications =
    applications?.filter(
      (app) =>
        matchesSearch(app.applicant?.fullName || "", searchQuery) ||
        matchesSearch(app.applicant?.email || "", searchQuery) ||
        matchesSearch(app.jobOpening?.title || "", searchQuery),
    ) ?? [];

  const columns = [
    {
      key: "applicant",
      header: "Candidato",
      cell: (app: ExtendedApplication) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-[hsl(var(--coral-mist))] text-[hsl(var(--comp-coral))] text-xs font-medium">
              {app.applicant?.fullName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "??"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{app.applicant?.fullName || "Sem nome"}</p>
            <p className="text-xs text-[hsl(var(--ink-muted))]">{app.applicant?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "position",
      header: "Vaga",
      cell: (app: ExtendedApplication) => app.jobOpening?.title || "-",
    },
    {
      key: "aiScore",
      header: "Score IA",
      cell: (app: ExtendedApplication) =>
        app.aiVettingScore !== null && app.aiVettingScore !== undefined ? (
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-16 bg-[hsl(var(--sand-divider))] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  app.aiVettingScore >= 70
                    ? "bg-[hsl(var(--forest-pulse))]"
                    : app.aiVettingScore >= 40
                      ? "bg-[hsl(var(--amber-state))]"
                      : "bg-[hsl(var(--alert-red))]"
                }`}
                style={{ width: `${app.aiVettingScore}%` }}
              />
            </div>
            <span className="text-xs text-[hsl(var(--ink-muted))] tabular-nums">
              {app.aiVettingScore}
            </span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "stage",
      header: "Etapa",
      cell: (app: ExtendedApplication) => <StatusBadge status={app.currentStage || "applied"} />,
    },
    {
      key: "applied",
      header: "Inscrito em",
      cell: (app: ExtendedApplication) => (
        <span className="tabular-nums">{formatDate(app.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      cell: (app: ExtendedApplication) => (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              setEditApp(app);
            }}
            data-testid={`button-edit-applicant-${app.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {app.currentStage === "interviewing" && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedApplication(app);
                setIsOfferOpen(true);
              }}
              data-testid={`button-create-offer-${app.id}`}
            >
              <Mail className="h-3.5 w-3.5 mr-1" />
              Criar oferta
            </Button>
          )}
          {app.offers?.some((o) => o.status === "approved") && (
            <Button
              size="sm"
              variant="default"
              className="h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                const offer = app.offers?.find((o) => o.status === "approved");
                if (offer) sendOffer.mutate(offer.id);
              }}
              data-testid={`button-send-offer-${app.id}`}
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Enviar oferta
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex-1 space-y-6">
      <PageHeader title="Candidatos" description="Acompanhe candidatos no processo seletivo">
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-application">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar candidato
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adicionar candidato</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="applicantName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome completo</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Jane Smith"
                            {...field}
                            data-testid="input-applicant-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="applicantEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email (opcional)</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="jane@email.com"
                            {...field}
                            data-testid="input-applicant-email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="applicantPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefone</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="+55 11 99999-9999"
                            {...field}
                            data-testid="input-applicant-phone"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="linkedinUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>LinkedIn URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://linkedin.com/in/..."
                            {...field}
                            data-testid="input-applicant-linkedin"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="jobOpeningId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vaga</FormLabel>
                        <SearchableSelect
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          options={(openJobs || []).map((job) => ({
                            value: job.id,
                            label: job.title,
                          }))}
                          placeholder="Selecionar vaga"
                          data-testid="select-applicant-position"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createApplication.isPending}
                    data-testid="button-submit-application"
                  >
                    {createApplication.isPending ? "Adicionando..." : "Adicionar candidato"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Dialog open={isOfferOpen} onOpenChange={setIsOfferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Criar oferta para {selectedApplication?.applicant?.fullName}</DialogTitle>
          </DialogHeader>
          <Form {...offerForm}>
            <form onSubmit={offerForm.handleSubmit(onOfferSubmit)} className="space-y-4">
              <FormField
                control={offerForm.control}
                name="salaryBase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Salário base (BRL)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="20000"
                        {...field}
                        data-testid="input-offer-salary"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={offerForm.control}
                  name="equityShares"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Equity (shares)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="1000"
                          {...field}
                          data-testid="input-offer-equity"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={offerForm.control}
                  name="signingBonus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Signing bonus (BRL)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="5000"
                          {...field}
                          data-testid="input-offer-bonus"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsOfferOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createOffer.isPending}
                  data-testid="button-submit-offer"
                >
                  {createOffer.isPending ? "Criando..." : "Criar oferta"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar candidatos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-applicants"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredApplications}
        isLoading={isLoading}
        emptyMessage="Nenhuma candidatura encontrada. Adicione seu primeiro candidato."
        testIdPrefix="application"
        onRowClick={(app) => {
          if (app.applicant?.id) {
            navigate(`/applicants/${app.applicant.id}`);
          }
        }}
      />

      <ApplicantDetailModal
        applicationId={selectedApplicationId}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        propertyDefinitions={[]}
        propertyValues={[]}
      />

      {editApp && (
        <EditApplicantDialog
          open={!!editApp}
          onOpenChange={(open) => {
            if (!open) setEditApp(null);
          }}
          applicationId={editApp.id}
          initialData={{
            applicantName: editApp.applicant?.fullName || "",
            applicantEmail: editApp.applicant?.email || "",
            applicantPhone: editApp.applicant?.phone || "",
            linkedinUrl: editApp.applicant?.linkedinUrl || "",
          }}
        />
      )}
    </div>
  );
}
